import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ClientCountryProvider } from './contexts/ClientCountryContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AppThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { PublicLayout } from './components/layout/PublicLayout';
import { LoginPage } from './pages/LoginPage';
import { SignUpPage } from './pages/SignUpPage';
import { OtpVerifyPage } from './pages/OtpVerifyPage';
import { MagicLinkPage } from './pages/MagicLinkPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { ChatPage } from './pages/ChatPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { ProfilePage } from './pages/ProfilePage';
import { WalletPage } from './pages/WalletPage';
import { TransactionsPage } from './pages/TransactionsPage';
import { AccountSettingsPage } from './pages/AccountSettingsPage';
import { CommunityPage } from './pages/CommunityPage';
import { CommunityJoinPage } from './pages/CommunityJoinPage';
import { CommunityInviteLandingPage } from './pages/CommunityInviteLandingPage';
import { HomePage } from './features/marketplace/HomePage';
import { ProductPage } from './features/marketplace/ProductPage';

import { AppointmentDetailPage } from './features/booking/AppointmentDetailPage';
import { AppointmentBookingPage } from './features/booking/AppointmentBookingPage';
import { AppointmentsRouteLayout } from './components/layout/AppointmentsRouteLayout';
import { AppointmentsRouteIndex } from './components/layout/AppointmentsRouteIndex';
import { AuthLayout } from './components/layout/AuthLayout';
import { StorePage } from './features/store/StorePage';
import { CanadaEsimPage } from './features/store/CanadaEsimPage';
import { EsimOrderPage } from './features/store/EsimOrderPage';
import { ProductCheckoutPage } from './features/store/ProductCheckoutPage';
import { CarListingPage } from './features/cars/CarListingPage';
import { CarDetailPage } from './features/cars/CarDetailPage';
import { CanadaOnlyRoute } from './components/CanadaOnlyRoute';
import MembershipPricingPage from './features/membership/MembershipPricingPage';
import MembershipGetStartedPage from './features/membership/MembershipGetStartedPage';
import MembershipUpgradePage from './features/membership/MembershipUpgradePage';
import { CompleteProfilePage } from './pages/CompleteProfilePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { WalletGuidePage } from './features/wallet-guide';
import { PaymentResultPage } from './pages/PaymentResultPage';

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public wallet guide — readable without login */}
      <Route path="/wallet-guide" element={<WalletGuidePage />} />

      {/* Public marketplace — browse without login */}
      <Route element={<PublicLayout />}>
        <Route path="/store/:slug" element={<ProductPage />} />
        <Route path="/appointments/:slug" element={<AppointmentDetailPage />} />
      </Route>

      {/* Appointments picker — sidebar when logged in, public header when guest */}
      <Route path="/appointments" element={<AppointmentsRouteLayout />}>
        <Route index element={<AppointmentsRouteIndex />} />
      </Route>

      {/* Protected app — chat, account, communities, booking, store */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="payment/result" element={<PaymentResultPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="community" element={<Navigate to="/chat" replace />} />
        <Route path="community/:communityId" element={<CommunityPage />} />
        <Route path="community/join/:token" element={<CommunityJoinPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="profile/wallet" element={<WalletPage />} />
        <Route path="profile/wallet/transactions" element={<TransactionsPage />} />
        <Route path="profile/settings" element={<AccountSettingsPage />} />

        {/* CRM Booking */}
        <Route path="appointments/:slug/book" element={<AppointmentBookingPage />} />

        {/* CRM Store */}
        <Route path="store" element={<StorePage />} />
        <Route path="store/canada-esim" element={<CanadaEsimPage />} />
        <Route path="store/canada-esim/orders/:saleId" element={<EsimOrderPage />} />
        <Route path="store/india-esim" element={<Navigate to="/store/canada-esim" replace />} />
        <Route path="store/india-esim/orders/:saleId" element={<Navigate to="/store/canada-esim" replace />} />
        <Route path="store/checkout/:slug" element={<ProductCheckoutPage />} />
        <Route
          path="vehicles"
          element={
            <CanadaOnlyRoute>
              <CarListingPage />
            </CanadaOnlyRoute>
          }
        />
        <Route
          path="vehicles/:vehicleId"
          element={
            <CanadaOnlyRoute>
              <CarDetailPage />
            </CanadaOnlyRoute>
          }
        />
        <Route path="membership" element={<MembershipPricingPage />} />
        <Route path="membership/upgrade" element={<MembershipUpgradePage />} />
        <Route path="membership/get-started" element={<MembershipGetStartedPage />} />
      </Route>

      {/* Auth pages */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/verify-otp" element={<OtpVerifyPage />} />
        <Route path="/verify-magic-link" element={<MagicLinkPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/community/invite/:token" element={<CommunityInviteLandingPage />} />
        <Route path="/complete-profile" element={<CompleteProfilePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AppThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <ClientCountryProvider>
            <Router>
              <AppRoutes />
            </Router>
          </ClientCountryProvider>
        </NotificationProvider>
      </AuthProvider>
    </AppThemeProvider>
  );
};

export default App;
